import { AdvertisingSlot } from 'react-advertising'
import styled from 'styled-components'

import { zIndexRegistry } from '@/constants/zIndexRegistry'
import { AD_SLOTS } from '@/features/advertisement/constants/adSlots'
import { MEDIA_QUERIES } from '@/ui/constants/mediaQueries'

const LEADERBOARD_SCROLL_AWAY_OFFSET = '0px'

type TopLeaderboardAdContainerProps = {
  isSticky: boolean
  shouldScrollAway: boolean
}

export const TopLeaderboardAdContainer = styled.div<TopLeaderboardAdContainerProps>(
  ({ theme, isSticky, shouldScrollAway }) => {
    return `
    overflow-x: hidden;
    pointer-events: auto;

    ${MEDIA_QUERIES(theme).medium}  {
      display: flex;
      min-height: ${AD_SLOTS.LEADERBOARD.HEIGHT};
      z-index: ${zIndexRegistry.TOP_LEADERBOARD};
      margin-top: ${theme.spacing.default};
    }

    ${MEDIA_QUERIES(theme).large}  {
      background: ${isSticky ? 'white' : 'transparent'};
      box-shadow: ${isSticky ? '0 0.2rem 0.5rem rgb(0 0 0 / 5%)' : 'none'};
      display: flex;
      min-height: ${AD_SLOTS.LEADERBOARD.HEIGHT};
      position: sticky;
      top: ${isSticky ? LEADERBOARD_SCROLL_AWAY_OFFSET : 'auto'};
      transform: translateY(${shouldScrollAway && isSticky ? '-50vh' : '0'});
      transition: ${isSticky && 'transform 0.8s ease-out'};
      align-items: center;
    }
  `
  }
)

export const StyledTopSlotWrapper = styled.div`
  margin: 0 auto;
`

export const InlineAdSlotWrapper = styled.li<{
  isAdPresent?: boolean
  isGridView?: boolean
}>(
  ({ theme, isAdPresent = true, isGridView = false }) => `
  display: flex;
  justify-content: center;
  overflow: hidden;

  /* the grid-column is added here to make the display ad take the full width of the grid */
  ${isGridView ? 'grid-column: 1 / -1;' : ''}

 ${isAdPresent ? `margin: -${theme.spacing.defaultSmall} 0;` : ''}

  /** The change should only happen for mweb, we should revert to the original on desktop */
  ${MEDIA_QUERIES(theme).medium} {
    margin: unset;
  }
`
)

export const StyledInlineAdSlot = styled(AdvertisingSlot)(
  ({ theme }) => `
  max-width: 100%;

  & iframe {
    max-width: 100%;
    overflow: hidden;
  }

  &[data-google-query-id] iframe { 
    margin: ${theme.spacing.default} auto;
  }

  ${MEDIA_QUERIES(theme).medium} {
    margin: 0 auto;
  }
`
)

export const StyledBaseLeaderboard = styled(AdvertisingSlot)(({ theme }) => {
  return `
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  max-width: ${AD_SLOTS.BASE_LEADERBOARD_S.WIDTH};
  max-height: ${AD_SLOTS.BASE_LEADERBOARD_S.HEIGHT};

  ${MEDIA_QUERIES(theme).medium}  {
    max-width: ${AD_SLOTS.LEADERBOARD.WIDTH};
    max-height: ${AD_SLOTS.LEADERBOARD.HEIGHT};
  }

  ${MEDIA_QUERIES(theme).large}  {
    max-width: ${AD_SLOTS.LEADERBOARD_XL.WIDTH};
    max-height: ${AD_SLOTS.LEADERBOARD_XL.HEIGHT};
  }
`
})

/**
 * This slot is for a custom ad unit that will be similar in styling & size to an organic listing.
 * The unit container is a fixed size.
 * AdOps haven't finalized this size yet, but we're currently testing 510x210px.
 * Added a new size 956, 208. so adding a new max-height for the case when adsense come along other ads
 * */
export const StyledMiniLeaderboard = styled(AdvertisingSlot)(({ theme }) => {
  return `
    margin: ${theme.spacing.default} auto 0;
    display: flex;
    overflow: hidden;

    width: 100%;

    ${MEDIA_QUERIES(theme).small}  {
      max-height: 9rem;
    }

    ${MEDIA_QUERIES(theme).medium}  {
      max-height: 15rem;
    }

    ${MEDIA_QUERIES(theme).large}  {
      max-height: 21rem;
    }

    &:empty {
      margin: 0;
    }
  `
})

export const StyledMobileLeaderboard = styled(AdvertisingSlot)(({ theme }) => {
  return `
  margin: ${theme.spacing.husky} auto;
  display: flex;
  justify-content: center;
  overflow: hidden;
  width: ${AD_SLOTS.MOBILE_LEADERBOARD.WIDTH};
  height: ${AD_SLOTS.MOBILE_LEADERBOARD.HEIGHT};
`
})

type StyledAdSenseSrpContainerProps = {
  position?: string
}

type SearchListingsWrapperProps = {
  canShowRightHandAd: boolean
}

/**
 * position prop is used to make sure we set min-height for top leader board (maintaining old logic)
 * Purpose - so that we don't cause layout shift for the top position of adsense.
 *
 * position => top ( for top leader board - adsense)
 * position => bottom ( for mini leader board - adsense)
 */

export const StyledAdSenseSrpContainer = styled.div<StyledAdSenseSrpContainerProps>(
  ({ theme, position }) => `
    background-color: ${theme.colors.white};
    border-radius: ${theme.borderRadius.medium};
    border: .1rem solid ${theme.colors.grey.light4};
    min-height: ${position === 'top' ? '30rem' : 'auto'}; 
    overflow: hidden;
    position: relative;

    ${MEDIA_QUERIES(theme).medium} {
      min-height: 21rem;
    }
  `
)

export const SkyscraperUpperRightAdSlotContainer = styled.div(
  ({ theme }) => `
    display: none;
    justify-content: center;
    overflow: hidden;
    width: 300px;
    margin-left: 1.5rem;
    position: sticky;
    top: 9rem;
    max-height: 600px;

    ${MEDIA_QUERIES(theme).xLarge}{
      display: flex;
    }
  `
)

export const SearchListingsWrapper = styled.div<SearchListingsWrapperProps>(
  ({ theme, canShowRightHandAd }) => `
    width: 100%;
    ${MEDIA_QUERIES(theme).xLarge}{
      ${canShowRightHandAd ? 'width: calc(100% - 300px - 1.5rem);' : 'width: 100%'}
    }
  `
)

export const StyledGptTakeover = styled(AdvertisingSlot)<{ $hasTakeover?: boolean }>(
  ({ theme, $hasTakeover }) => `
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 0;
  pointer-events: none;
  visibility: hidden;
  opacity: 0;

  display: none;

  ${MEDIA_QUERIES(theme).large} {
    display: block;
    visibility: ${$hasTakeover ? 'visible' : 'hidden'};
    opacity: ${$hasTakeover ? '1' : '0'};
  }

  &[data-google-query-id] {
    pointer-events: auto;
  }

  &[data-google-query-id] iframe {
    width: 100vw !important;
    height: 100vh !important;
    object-fit: cover;
  }
`
)
